import WhatsAppContactButton from '../components/whatsapp/WhatsAppContactButton';
import { CrmRecordHeader, CrmSection } from '../components/crm/CrmRecordLayout';
import { CrmTabs } from '../components/crm/CrmUi';
import { useCrmNavigation } from '../hooks/useCrmNavigation';
import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useClient, useClientStats } from '@offertify/query';
import { clientsAPI, geocodingAPI } from '../utils/api';
import { usePermissions } from '../hooks/usePermissions';
import { ArrowLeft, AlertCircle, Pencil } from 'lucide-react';
import ClientProfileCard from '../components/client/ClientProfileCard';
import ClientMap from '../components/client/ClientMap';
import ClientKPICards from '../components/client/ClientKPICards';
import ClientDocumentsTab from '../components/client/ClientDocumentsTab';
import ClientOffersTab from '../components/client/ClientOffersTab';
import ClientTimelineTab from '../components/client/ClientTimelineTab';
import ClientVisiteTab from '../components/client/ClientVisiteTab';
import ClientEditForm from '../components/client/ClientEditForm';
import ClientPortalTab from '../components/client/ClientPortalTab';

type Tab = 'profilo' | 'panoramica' | 'offerte' | 'ordini' | 'ddt' | 'fatture' | 'visite' | 'timeline' | 'portale';

const TABS: { key: Tab; label: string }[] = [
  { key: 'profilo', label: 'Anagrafica' },
  { key: 'panoramica', label: 'Riepilogo commerciale' },
  { key: 'offerte', label: 'Offerte' },
  { key: 'ordini', label: 'Ordini' },
  { key: 'ddt', label: 'DDT' },
  { key: 'fatture', label: 'Fatture' },
  { key: 'visite', label: 'Visite & Attivit\u00e0' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'portale', label: 'Portale' },
];

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { returnTo } = useCrmNavigation('/clients');
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('clienti:edit');
  const [params, setParams] = useSearchParams();
  const activeTab = TABS.some(t => t.key === params.get('tab')) && (!['offerte', 'ordini', 'ddt', 'fatture'].includes(params.get('tab') || '') || hasPermission('offerte:view')) ? params.get('tab') as Tab : 'profilo';
  const setActiveTab = (tab: Tab) => { const next = new URLSearchParams(params); next.set('tab', tab); setParams(next); };
  const canViewSales = hasPermission('offerte:view');
  const [isEditing, setIsEditing] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const geocodeAttempted = useRef<string | null>(null);

  const {
    data: client,
    isLoading: isLoadingClient,
    isError: isClientError,
    refetch: refetchClient,
  } = useClient(id);

  const { data: stats, isLoading: isLoadingStats } = useClientStats(id);

  // Auto-geocode when client has address but no coordinates
  useEffect(() => {
    if (!client || !id) return;
    if (client.latitudine != null && client.longitudine != null) return;

    const hasAddress = client.indirizzo || client.citta;
    if (!hasAddress) return;

    // Build a key to avoid re-geocoding the same address
    const addressKey = [client.indirizzo, client.citta, client.provincia, client.cap].join('|');
    if (geocodeAttempted.current === addressKey) return;
    geocodeAttempted.current = addressKey;

    const doGeocode = async () => {
      setIsGeocoding(true);
      try {
        const res = await geocodingAPI.geocode({
          indirizzo: client.indirizzo,
          citta: client.citta,
          provincia: client.provincia,
          cap: client.cap,
        });
        const coords = res.data.data;
        if (coords?.lat != null && coords?.lng != null) {
          await clientsAPI.updateClient(id, {
            latitudine: coords.lat,
            longitudine: coords.lng,
          });
          refetchClient();
        }
      } catch {
        // Silently fail - map will show "not found"
      } finally {
        setIsGeocoding(false);
      }
    };

    doGeocode();
  }, [client, id, refetchClient]);

  // Loading state
  if (isLoadingClient) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="h-8 w-24 bg-gray-200 rounded" />
            <div className="h-8 w-64 bg-gray-200 rounded" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="h-64 bg-gray-200 rounded-lg" />
            <div className="lg:col-span-2 space-y-4">
              <div className="h-52 bg-gray-200 rounded-lg" />
              <div className="grid grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-24 bg-gray-200 rounded-lg" />
                ))}
              </div>
            </div>
          </div>
          <div className="h-96 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  // Error state
  if (isClientError || !client) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Cliente non trovato
          </h2>
          <p className="text-gray-500 mb-6">
            Il cliente richiesto non esiste o non hai i permessi per visualizzarlo.
          </p>
          <Link
            to={returnTo}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna alla lista clienti
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <CrmRecordHeader kind="client" record={client} backTo={returnTo}
        badges={<span className="text-xs font-medium text-gray-500">{client.tipo === 'azienda' ? 'Azienda' : 'Persona fisica'}</span>}
        actions={!isEditing && canEdit && <button onClick={() => { setActiveTab('profilo'); setIsEditing(true); }} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"><Pencil className="h-4 w-4" />Modifica anagrafica</button>}
      />
      <WhatsAppContactButton phone={client.telefono} />
      {/* Tab bar */}
      <CrmTabs tabs={TABS.filter(t => canViewSales || !['offerte', 'ordini', 'ddt', 'fatture'].includes(t.key))} active={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      <div hidden={activeTab !== 'profilo'}>
        {isEditing ? <ClientEditForm client={client} onUpdate={() => { geocodeAttempted.current = null; refetchClient(); setIsEditing(false); }} onCancel={() => setIsEditing(false)} /> : <ClientProfileCard client={client} />}
      </div>
      {activeTab === 'panoramica' && <div className="space-y-6">
        <ClientKPICards stats={stats} isLoading={isLoadingStats} />
        <div className="rounded-xl border border-gray-200 bg-white"><CrmSection title="Sede cliente" description="Posizione dell’indirizzo registrato."><ClientMap indirizzo={client.indirizzo} citta={client.citta} provincia={client.provincia} cap={client.cap} latitudine={client.latitudine} longitudine={client.longitudine} isGeocoding={isGeocoding} /></CrmSection></div>
      </div>}
      <div>
        {canViewSales && activeTab === 'offerte' && <ClientOffersTab key={id} clientId={id!} />}
        {canViewSales && activeTab === 'ordini' && <ClientDocumentsTab key={`${id}:ordini`} clientId={id!} kind="ordine" />}
        {canViewSales && activeTab === 'ddt' && <ClientDocumentsTab key={`${id}:ddt`} clientId={id!} kind="ddt" />}
        {canViewSales && activeTab === 'fatture' && <ClientDocumentsTab key={`${id}:fatture`} clientId={id!} kind="fattura" />}
        {activeTab === 'visite' && <ClientVisiteTab clientId={id!} />}
        {activeTab === 'timeline' && <ClientTimelineTab clientId={id!} />}
        {activeTab === 'portale' && (
          <ClientPortalTab client={client} onUpdate={() => refetchClient()} />
        )}
      </div>
    </div>
  );
};

export default ClientDetail;
